import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, Play, Pause, Volume2, VolumeX, Check, Upload, Image, Film, Crown,
  Save, FolderOpen, Trash2, Plus, ChevronDown, Clock, AlertCircle, Loader2, Target,
  Settings2, Mic
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { trackVisualisationCompleted } from '../lib/analytics';
import { hapticLight, hapticSuccess } from '../lib/nativeFeel';
import { useAudioInterruption } from '../lib/nativeAudio';
import {
  uploadFileChunked,
  readVideoDuration,
  isSupportedVideoFile,
} from '../lib/chunkedUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ambient sound options - using reliable, working URLs
const AMBIENT_SOUNDS = [
  {
    id: 'none',
    name: 'No Background Sound',
    category: 'Silent',
    url: null,
  },
  {
    id: 'patience',
    name: 'Patience 528Hz',
    category: 'Healing',
    url: 'https://customer-assets.emergentagent.com/job_identity-tracker-12/artifacts/llu1v94x_Patience%20%28528%20Hz%29.mp3',
  },
  {
    id: 'horizon',
    name: 'After the Horizon',
    category: '432Hz',
    url: 'https://customer-assets.emergentagent.com/job_identity-tracker-12/artifacts/eddlpp82_After%20the%20Horizon%20%28432%20Hz%29.mp3',
  },
  {
    id: 'violet',
    name: 'Violet Flame',
    category: '432Hz',
    url: 'https://customer-assets.emergentagent.com/job_identity-tracker-12/artifacts/yj95zmok_Violet%20Flame%20Meditation%20%28432%20Hz%29.mp3',
  },
];

// Background options - removed any non-working images, added better starry night
const BLANK_BG = { id: 'none', name: 'No background', url: null };
const savedBgFullUrl = (bg) => (bg.file_url.startsWith('http') ? bg.file_url : `${API_URL}${bg.file_url}`);
const BACKGROUNDS = [
  {
    id: 'stars',
    name: 'Starry Night',
    url: 'https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'beach',
    name: 'Beach Sunset',
    url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'forest',
    name: 'Forest',
    url: 'https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'mountain',
    name: 'Mountain Range',
    url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'aurora',
    name: 'Aurora Borealis',
    url: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'lake',
    name: 'Calm Lake',
    url: 'https://images.unsplash.com/photo-1439066615861-d1af74d74000?auto=format&fit=crop&w=1920&q=80',
  },
];

// Timer durations
const TIMER_OPTIONS = [
  { label: '3 min', value: 180 },
  { label: '5 min', value: 300 },
  { label: '10 min', value: 600 },
  { label: '15 min', value: 900 },
  { label: '20 min', value: 1200 },
];

// Slideshow intervals
const SLIDESHOW_INTERVALS = [
  { label: '5s', value: 5 },
  { label: '10s', value: 10 },
  { label: '15s', value: 15 },
  { label: '30s', value: 30 },
];

// Video upload constraints — hard client-side gates enforced BEFORE upload begins.
// These match the backend's MAX_FILE_SIZE_MB / MAX_VIDEO_DURATION_SECONDS.
const MAX_VIDEO_SIZE_MB = 100;
const MAX_VIDEO_DURATION_SECONDS = 90;  // 1 minute 30 seconds
// Accepted MIME prefix + extension coverage lives in ../lib/chunkedUpload
// (isSupportedVideoFile). Keeping the whitelist there means the frontend
// gate and the /init payload agree on what counts as a "video".

const Visualization = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [goal, setGoal] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);
  const [selectedDuration, setSelectedDuration] = useState(180);
  const [selectedSound, setSelectedSound] = useState(AMBIENT_SOUNDS[1]);
  const [selectedBg, setSelectedBg] = useState(BACKGROUNDS[0]);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Audio state
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [audioReady, setAudioReady] = useState(false);
  
  // Slideshow state
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slideshowInterval, setSlideshowInterval] = useState(10);
  const [slideshowActive, setSlideshowActive] = useState(false);
  
  // Custom media state
  const [customVideo, setCustomVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Presets state
  const [presets, setPresets] = useState([]);
  const [showPresetsModal, setShowPresetsModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [showSoundPicker, setShowSoundPicker] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Today's affirmation state
  const [todaysAffirmation, setTodaysAffirmation] = useState(null);
  const [todaysAffirmationId, setTodaysAffirmationId] = useState(null);
  
  // TTS Voice state (Premium)
  const [voiceAudioUrl, setVoiceAudioUrl] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voicePlaying, setVoicePlaying] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  
  // Multiple goals state (for premium users)
  const [allGoals, setAllGoals] = useState([]);
  const [showGoalPicker, setShowGoalPicker] = useState(false);
  
  // Saved backgrounds state (Premium - persistent uploads)
  const [savedBackgrounds, setSavedBackgrounds] = useState([]);
  const [savedBgSlots, setSavedBgSlots] = useState({ used: 0, available: 3 });
  const [savedBgLoading, setSavedBgLoading] = useState(false);
  const [uploadingSavedBg, setUploadingSavedBg] = useState(false);
  const [selectedSavedBg, setSelectedSavedBg] = useState(null); // Track selected saved background
  const [activeSavedBgId, setActiveSavedBgId] = useState(null); // background_id of the displayed saved background
  const [selectedBackgroundIdForGoal, setSelectedBackgroundIdForGoal] = useState(null);
  const replaceInputRef = useRef(null);
  const replaceTargetIdRef = useRef(null);

  // Chunked upload progress + control (real byte-level progress, cancel, phase)
  const [uploadPhase, setUploadPhase] = useState(null); // 'preparing' | 'uploading' | 'finalizing'
  const activeUploadRef = useRef(null); // { cancel, key } — prevents double-tap concurrency
  
  const timerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const audioRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const videoRef = useRef(null);
  const slideshowRef = useRef(null);
  const savedBgFileInputRef = useRef(null);

  // Calculate progress percentage
  const progress = ((selectedDuration - timeLeft) / selectedDuration) * 100;

  // Pause audio/video when the app is backgrounded (native app-state,
  // browser visibility) and resume on return, but only if the user hadn't
  // paused manually. Zero-op on the web when the tab stays visible.
  useAudioInterruption({
    getRefs: () => [audioRef, voiceAudioRef, videoRef],
    isSessionActive: () => isPlaying,
  });

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get current background URL
  const getCurrentBgUrl = () => {
    // Priority: selectedSavedBg > slideshow > single uploaded image > selectedBg
    if (selectedSavedBg) {
      return selectedSavedBg;
    }
    if (slideshowActive && slideshowImages.length > 0) {
      return slideshowImages[currentSlideIndex];
    }
    // Single uploaded image (slideshowActive=false but image present)
    if (!slideshowActive && slideshowImages.length === 1) {
      return slideshowImages[0];
    }
    return selectedBg.url;
  };

  // Fetch goal, presets, and today's affirmation on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [goalRes, presetsRes, affirmationRes, allGoalsRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/goals/active`, { credentials: 'include' }),
          fetch(`${API_URL}/api/visualization/presets`, { credentials: 'include' }),
          fetch(`${API_URL}/api/affirmations/today`, { credentials: 'include' }),
          fetch(`${API_URL}/api/goals`, { credentials: 'include' }),
          fetch(`${API_URL}/api/visualisation/settings`, { credentials: 'include' })
        ]);
        
        if (goalRes.ok) {
          const data = await goalRes.json();
          setGoal(data);
        }
        
        if (presetsRes.ok) {
          const data = await presetsRes.json();
          setPresets(data.presets || []);
        }
        
        if (affirmationRes.ok) {
          const data = await affirmationRes.json();
          if (data.affirmation) {
            setTodaysAffirmation(data.affirmation.content);
            setTodaysAffirmationId(data.affirmation.affirmation_id);
          }
        }
        
        if (allGoalsRes.ok) {
          const data = await allGoalsRes.json();
          setAllGoals(data || []);
        }

        // Restore last-used visualisation settings (auto-load preferences)
        if (settingsRes.ok) {
          const s = await settingsRes.json();
          if (s.timer_duration) {
            setSelectedDuration(s.timer_duration);
            setTimeLeft(s.timer_duration);
          }
          if (s.music_id) {
            const sound = AMBIENT_SOUNDS.find(snd => snd.id === s.music_id);
            if (sound) setSelectedSound(sound);
          }
          if (s.background_id && s.background_type === 'default') {
            const bg = BACKGROUNDS.find(b => b.id === s.background_id);
            if (bg) setSelectedBg(bg);
          }
        }
      } catch (error) {
        console.error('[Visualization] Failed to load initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    return () => {
      // Cleanup on unmount
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        voiceAudioRef.current.src = '';
        voiceAudioRef.current = null;
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (slideshowRef.current) clearInterval(slideshowRef.current);
    };
  }, []);

  // Auto-save visualisation settings (debounced) whenever user changes core preferences.
  // Skips the initial mount so we don't overwrite restored settings.
  const settingsLoadedRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    // First render after data load: mark loaded but don't save yet
    if (!settingsLoadedRef.current) {
      settingsLoadedRef.current = true;
      return;
    }
    const handle = setTimeout(() => {
      fetch(`${API_URL}/api/visualisation/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          timer_duration: selectedDuration,
          music_id: selectedSound?.id,
          background_id: selectedBg?.id === BLANK_BG.id ? undefined : selectedBg?.id,
          background_type: 'default',
        })
      }).catch(() => { /* silent: non-critical */ });
    }, 800);
    return () => clearTimeout(handle);
  }, [selectedDuration, selectedSound, selectedBg, loading]);

  // Fetch available TTS voices for premium users
  useEffect(() => {
    const fetchVoices = async () => {
      if (!user?.is_premium) return;
      
      try {
        const response = await fetch(`${API_URL}/api/tts/voices`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableVoices(data.voices || []);
          // Set default voice
          if (data.default_voice_id && data.voices) {
            const defaultVoice = data.voices.find(v => v.voice_id === data.default_voice_id);
            if (defaultVoice) {
              setSelectedVoice(defaultVoice);
            }
          }
        }
      } catch (error) {
        console.error('[Visualization] Failed to fetch TTS voices:', error);
      }
    };
    fetchVoices();
  }, [API_URL, user?.is_premium]);

  // Fetch saved backgrounds for premium users (source of truth for slots).
  // Extracted from the effect so it can be called after any mutation
  // (upload / delete / replace) — this keeps local state perfectly in sync
  // with the server and avoids drift that used to block the 2nd/3rd upload.
  const fetchSavedBackgrounds = useCallback(async () => {
    if (!user?.is_premium) return;
    setSavedBgLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/backgrounds/saved`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSavedBackgrounds(data.data.backgrounds || []);
          setSavedBgSlots({
            used: data.data.slots_used || 0,
            available: data.data.slots_available ?? 3,
          });
          setSelectedBackgroundIdForGoal(data.data.selected_background_id || null);
        }
      }
    } catch (error) {
      // Silent — non-fatal; the UI will re-render on the next successful call.
    } finally {
      setSavedBgLoading(false);
    }
  }, [API_URL, user?.is_premium]);

  useEffect(() => {
    fetchSavedBackgrounds();
  }, [fetchSavedBackgrounds, goal?.goal_id]);

  // Robust audio handling with proper event listeners
  useEffect(() => {
    // Define handlers at the top of useEffect scope so they're available for cleanup
    let currentAudioHandlers = {
      ready: null,
      error: null
    };

    // Clean up previous audio instance
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }

    setAudioError(null);
    setAudioReady(false);

    if (selectedSound && selectedSound.url && isPlaying && !isMuted) {
      setAudioLoading(true);
      
      const audio = new Audio();
      audioRef.current = audio;
      
      // Configure audio element
      audio.preload = 'auto';
      audio.loop = true;
      audio.volume = 0.5;
      // Note: Not setting crossOrigin to avoid CORS issues with audio CDNs

      // Define handlers and store references for cleanup
      currentAudioHandlers.ready = function onAudioReady() {
        setAudioLoading(false);
        setAudioReady(true);
        setAudioError(null);
        
        // Attempt playback with user interaction context
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
            })
            .catch((error) => {
              // Handle autoplay restrictions
              if (error.name === 'NotAllowedError') {
                setAudioError('Tap screen to enable audio');
              } else {
                setAudioError('Audio playback failed');
              }
            });
        }
      };

      currentAudioHandlers.error = function onAudioError(e) {
        setAudioLoading(false);
        setAudioReady(false);
        setAudioError('Failed to load audio');
      };

      audio.addEventListener('canplaythrough', currentAudioHandlers.ready, { once: true });
      audio.addEventListener('error', currentAudioHandlers.error);
      
      // Set source and begin loading
      audio.src = selectedSound.url;
      audio.load();
    }

    // Cleanup function
    return () => {
      if (audioRef.current) {
        // Safely remove event listeners if they exist
        if (currentAudioHandlers.ready) {
          audioRef.current.removeEventListener('canplaythrough', currentAudioHandlers.ready);
        }
        if (currentAudioHandlers.error) {
          audioRef.current.removeEventListener('error', currentAudioHandlers.error);
        }
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, [selectedSound, isPlaying, isMuted]);

  // Handle mute/unmute
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
      if (!isMuted && isPlaying && audioReady) {
        audioRef.current.play().catch(console.error);
      }
    }
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, isPlaying, audioReady]);

  // Handle slideshow
  useEffect(() => {
    if (slideshowActive && slideshowImages.length > 1 && isPlaying) {
      slideshowRef.current = setInterval(() => {
        setCurrentSlideIndex(prev => (prev + 1) % slideshowImages.length);
      }, slideshowInterval * 1000);
    }

    return () => {
      if (slideshowRef.current) clearInterval(slideshowRef.current);
    };
  }, [slideshowActive, slideshowImages.length, slideshowInterval, isPlaying]);

  // Generate or fetch TTS audio for affirmation (Premium feature)
  const generateVoiceAudio = useCallback(async () => {
    if (!user?.is_premium || !todaysAffirmation || !todaysAffirmationId) {
      return null;
    }

    // Handle "No Voice" / Silent mode - skip audio generation
    if (selectedVoice?.is_silent || selectedVoice?.voice_id === 'none') {
      return null;
    }

    // Create a unique cache key including voice_id
    const voiceId = selectedVoice?.voice_id || '';
    const cacheKey = `${todaysAffirmationId}_${voiceId}`;

    // If we already have the audio URL for this voice, return it
    if (voiceAudioUrl && voiceAudioUrl.cacheKey === cacheKey) {
      return voiceAudioUrl.url;
    }

    setVoiceLoading(true);
    setVoiceError(null);

    try {
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          affirmation_id: `${todaysAffirmationId}_${voiceId}`, // Include voice in cache key
          text: todaysAffirmation,
          voice_id: selectedVoice?.voice_id || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Handle silent response from backend
        if (data.silent) {
          return null;
        }
        setVoiceAudioUrl({ url: data.audio_url, cacheKey });
        return data.audio_url;
      } else {
        const error = await response.json();
        setVoiceError(error.detail || 'Failed to generate voice');
        return null;
      }
    } catch (error) {
      setVoiceError('Voice generation failed');
      return null;
    } finally {
      setVoiceLoading(false);
    }
  }, [API_URL, user?.is_premium, todaysAffirmation, todaysAffirmationId, voiceAudioUrl, selectedVoice]);

  // Play the voice affirmation audio
  const playVoiceAffirmation = useCallback(async (audioUrl) => {
    return new Promise((resolve) => {
      if (!audioUrl) {
        resolve();
        return;
      }

      // Clean up previous voice audio
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        voiceAudioRef.current = null;
      }

      const voiceAudio = new Audio(audioUrl);
      voiceAudioRef.current = voiceAudio;
      voiceAudio.volume = 0.9;

      setVoicePlaying(true);

      voiceAudio.onended = () => {
        setVoicePlaying(false);
        resolve();
      };

      voiceAudio.onerror = (e) => {
        setVoicePlaying(false);
        setVoiceError('Voice playback failed');
        resolve();
      };

      voiceAudio.play().catch((e) => {
        setVoicePlaying(false);
        resolve();
      });
    });
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleComplete = async () => {
    setIsPlaying(false);
    setCompleted(true);
    setShowControls(true);
    // Fire the approved event (no PII)
    trackVisualisationCompleted({
      duration_seconds: typeof selectedDuration === 'number' ? selectedDuration : 0,
      has_voice: !!voiceAudioUrl,
      has_music: !!selectedSound && selectedSound.id !== 'none',
      has_background: !!(selectedSavedBg || customVideo || slideshowImages.length > 0),
      has_slideshow: slideshowActive && slideshowImages.length > 1,
      has_video: !!customVideo,
    });
    
    // Safely pause audio
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch (e) {
        console.error('[Visualization] Audio pause failed on complete:', e);
      }
    }
    
    // Clear slideshow interval
    if (slideshowRef.current) {
      clearInterval(slideshowRef.current);
      slideshowRef.current = null;
    }
    
    try {
      await fetch(`${API_URL}/api/visualization/complete?duration_seconds=${selectedDuration}`, {
        method: 'POST',
        credentials: 'include',
      });
      toast.success('Visualization complete! Well done.');
      hapticSuccess();
    } catch (error) {
      console.error('[Visualization] Failed to record completion:', error);
    }
  };

  const togglePlay = async () => {
    if (completed) {
      setCompleted(false);
      setTimeLeft(selectedDuration);
    }
    
    if (isPlaying) {
      // Pausing
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsPlaying(false);
      
      // Safely pause video
      if (videoRef.current) {
        try {
          videoRef.current.pause();
        } catch (e) {
          console.error('[Visualization] Video pause failed:', e);
        }
      }
      
      // Safely pause audio
      if (audioRef.current) {
        try {
          audioRef.current.pause();
        } catch (e) {
          console.error('[Visualization] Audio pause failed:', e);
        }
      }
      
      // Safely pause voice audio
      if (voiceAudioRef.current) {
        try {
          voiceAudioRef.current.pause();
        } catch (e) {
          console.error('[Visualization] Voice audio pause failed:', e);
        }
      }
      setVoicePlaying(false);
    } else {
      // Playing
      setIsPlaying(true);
      hapticLight();
      startTimer();
      
      // Safely play video
      if (videoRef.current) {
        try {
          videoRef.current.play().catch(e => console.error('[Video] Play error:', e));
        } catch (e) {
          console.error('[Visualization] Video play threw synchronously:', e);
        }
      }
      
      // For premium users with affirmation: kick off TTS in the background so the
      // visualisation session starts immediately. Voice plays as soon as it's ready.
      if (user?.is_premium && todaysAffirmation && todaysAffirmationId) {
        generateVoiceAudio()
          .then((audioUrl) => {
            // If the session is still active by the time audio is ready, play it.
            if (audioUrl) {
              playVoiceAffirmation(audioUrl);
            }
          })
          .catch(() => {
            // Errors are already surfaced via voiceError state; don't block session.
          });
      }
      
      // Audio will auto-play via useEffect when isPlaying becomes true
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  };

  const handleScreenTap = () => {
    setShowControls(true);
    
    // Try to resume audio on tap (handles autoplay restrictions)
    if (audioRef.current && isPlaying && !isMuted && audioReady) {
      try {
        audioRef.current.play().catch(e => console.error('[Audio] Resume error:', e));
      } catch (e) {
        console.error('[Visualization] Audio resume threw synchronously:', e);
      }
    }
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  };

  const handleMuteToggle = (e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  };

  // PREMIUM: Upload a file to backend using CHUNKED upload → Emergent Object Storage.
  // Streams the file to disk one 5MB chunk at a time so mobile uploads survive
  // brief network drops. Reports REAL byte-level progress, exposes cancel(),
  // and dedupes duplicate taps via activeUploadRef.
  //
  // opts:
  //   mediaType        – 'video' | 'image' (required)
  //   durationSeconds  – optional; for videos
  //   onProgress       – (pct: 0-100, info: {phase, loaded, total}) => void
  //   maxRetries       – forwarded to chunkedUpload (default 3 per chunk)
  const uploadPersistentMedia = async (file, displayName, opts = {}) => {
    const {
      mediaType = file.type.startsWith('video/') ? 'video' : 'image',
      durationSeconds,
      onProgress,
      maxRetries = 3,
      replaceBackgroundId,
    } = opts;

    // Duplicate-tap protection: if the user re-triggers while an upload is
    // still running for the SAME file (name+size), reuse the same promise.
    const key = `${file.name}::${file.size}::${file.lastModified || 0}::${replaceBackgroundId || ''}`;
    if (activeUploadRef.current && activeUploadRef.current.key === key) {
      return activeUploadRef.current.promise;
    }
    if (activeUploadRef.current && activeUploadRef.current.cancel) {
      // A different file is uploading — cancel it so slot state stays correct.
      try { activeUploadRef.current.cancel(); } catch (_) {}
    }

    const controller = uploadFileChunked(file, {
      name: displayName || (file.name || 'Upload').replace(/\.[^/.]+$/, ''),
      mediaType,
      durationSeconds,
      maxRetries,
      onProgress,
      replaceBackgroundId,
    });

    activeUploadRef.current = { key, cancel: controller.cancel, promise: controller.promise };
    try {
      const record = await controller.promise;
      return record;
    } finally {
      if (activeUploadRef.current && activeUploadRef.current.key === key) {
        activeUploadRef.current = null;
      }
    }
  };

  // Cancel any in-flight upload (invoked by the ✕ button in the progress card).
  const cancelActiveUpload = () => {
    if (activeUploadRef.current && activeUploadRef.current.cancel) {
      try { activeUploadRef.current.cancel(); } catch (_) {}
    }
  };

  // Auto-select an uploaded saved background as the active visualisation media
  const activateSavedBackground = (savedBg) => {
    const fullUrl = savedBg.file_url.startsWith('http')
      ? savedBg.file_url
      : `${API_URL}${savedBg.file_url}`;
    // Clear any in-session ephemeral media so the persistent media takes over
    slideshowImages.forEach(url => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });
    if (customVideo && customVideo.startsWith('blob:')) URL.revokeObjectURL(customVideo);
    setSlideshowImages([]);
    setSlideshowActive(false);
    setActiveSavedBgId(savedBg.background_id);
    if (savedBg.media_type === 'video') {
      setCustomVideo(fullUrl);
      setSelectedSavedBg(null);
    } else {
      setCustomVideo(null);
      setSelectedSavedBg(fullUrl);
    }
  };

  const clearCustomMedia = () => {
    slideshowImages.forEach(url => URL.revokeObjectURL(url));
    if (customVideo) URL.revokeObjectURL(customVideo);
    setSlideshowImages([]);
    setCustomVideo(null);
    setSlideshowActive(false);
    setVideoLoading(false);
    setUploadProgress(0);
    setSelectedSavedBg(null); // Clear selected saved background
    setActiveSavedBgId(null);
  };

  // Upload a new saved background (Premium - persistent, chunked)
  const handleUploadSavedBackground = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = isSupportedVideoFile(file);

    if (!isImage && !isVideo) {
      toast.error('Please upload an image or video file (MP4, MOV, M4V, WebM).');
      return;
    }

    // Check file size (100MB max per user spec)
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_VIDEO_SIZE_MB) {
      toast.error(`File too large. Max ${MAX_VIDEO_SIZE_MB}MB allowed.`);
      return;
    }

    // Show upload state IMMEDIATELY so picking a file always gives feedback.
    setUploadingSavedBg(true);
    setUploadProgress(0);
    setUploadPhase('preparing');

    // Duration gate for videos (readVideoDuration has an internal timeout so
    // it can never hang silently on iOS).
    let duration = null;
    if (isVideo) {
      duration = await readVideoDuration(file).catch(() => null);
      if (duration && duration > MAX_VIDEO_DURATION_SECONDS + 0.5) {
        toast.error(`Video too long. Max ${MAX_VIDEO_DURATION_SECONDS}s (got ${Math.round(duration)}s).`);
        setUploadingSavedBg(false);
        setUploadPhase(null);
        return;
      }
    }

    try {
      setUploadPhase('uploading');
      setUploadProgress(0);
      const saved = await uploadPersistentMedia(
        file,
        file.name.replace(/\.[^/.]+$/, ''),
        {
          mediaType: isVideo ? 'video' : 'image',
          durationSeconds: duration || null,
          onProgress: (pct, info) => {
            setUploadProgress(Math.round(pct));
            if (info?.phase === 'finalizing') setUploadPhase('finalizing');
          },
        },
      );
      // Refresh from server so slot counts + list stay in perfect sync
      // with the source of truth — this is what fixes the "can't upload
      // the 2nd/3rd file" bug caused by manual local state drift.
      await fetchSavedBackgrounds();
      toast.success('Background saved successfully!');
    } catch (error) {
      if (error && error.aborted) {
        toast.info('Upload cancelled.');
      } else {
        toast.error(error.message || 'Failed to upload background');
      }
    } finally {
      setUploadingSavedBg(false);
      setUploadPhase(null);
      setUploadProgress(0);
      // Reset file input so the same file can be reselected
      if (savedBgFileInputRef.current) {
        savedBgFileInputRef.current.value = '';
      }
    }
  };

  // Delete a saved background
  const handleDeleteSavedBackground = async (backgroundId) => {
    try {
      const response = await fetch(`${API_URL}/api/backgrounds/saved/${backgroundId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh from server so slot counts stay in perfect sync.
          await fetchSavedBackgrounds();
          toast.success('Background deleted');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete background');
      }
    } catch (error) {
      toast.error('Failed to delete background');
    }
  };

  // Select a saved background — also persists the choice for the active goal
  // so switching between goals restores the right background.
  const selectSavedBackground = async (bg) => {
    clearCustomMedia();
    const fullUrl = bg.file_url.startsWith('http') ? bg.file_url : `${API_URL}${bg.file_url}`;
    if (bg.media_type === 'video') {
      setCustomVideo(fullUrl);
      setSelectedSavedBg(null); // Video uses customVideo state
    } else {
      setSelectedSavedBg(fullUrl); // Image uses selectedSavedBg state
    }
    setSelectedBackgroundIdForGoal(bg.background_id);
    setActiveSavedBgId(bg.background_id);
    toast.success(`Background "${bg.name}" selected`);

    // Persist per-goal selection (fire-and-forget so UI stays snappy).
    if (goal?.goal_id) {
      try {
        await fetch(`${API_URL}/api/goals/${goal.goal_id}/selected-background`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ background_id: bg.background_id }),
        });
      } catch (_e) {
        // Non-fatal — the visual selection is still correct locally.
      }
    }
  };

  // Trigger the OS file picker in "replace" mode for a specific tile.
  const openReplacePicker = (backgroundId) => {
    replaceTargetIdRef.current = backgroundId;
    replaceInputRef.current?.click();
  };

  // Handle a file chosen from the replace picker.
  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    const replaceId = replaceTargetIdRef.current;
    replaceTargetIdRef.current = null;
    if (!file || !replaceId) return;

    const isImage = file.type.startsWith('image/');
    const isVideo = isSupportedVideoFile(file);
    if (!isImage && !isVideo) {
      toast.error('Please choose an image or video file (MP4, MOV, M4V, WebM).');
      return;
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_VIDEO_SIZE_MB) {
      toast.error(`File too large. Max ${MAX_VIDEO_SIZE_MB}MB allowed.`);
      return;
    }
    // Show upload state IMMEDIATELY so picking a file always gives feedback.
    setUploadingSavedBg(true);
    setUploadProgress(0);
    setUploadPhase('preparing');

    let duration = null;
    if (isVideo) {
      duration = await readVideoDuration(file).catch(() => null);
      if (duration && duration > MAX_VIDEO_DURATION_SECONDS + 0.5) {
        toast.error(`Video too long. Max ${MAX_VIDEO_DURATION_SECONDS}s (got ${Math.round(duration)}s).`);
        setUploadingSavedBg(false);
        setUploadPhase(null);
        return;
      }
    }

    try {
      setUploadPhase('uploading');
      setUploadProgress(0);
      // The backend uploads the NEW file first, then soft-deletes the old row.
      // If anything fails, the old row survives untouched.
      const saved = await uploadPersistentMedia(file, null, {
        mediaType: isVideo ? 'video' : 'image',
        durationSeconds: duration || null,
        replaceBackgroundId: replaceId,
        onProgress: (pct, info) => {
          setUploadProgress(Math.round(pct));
          if (info?.phase === 'finalizing') setUploadPhase('finalizing');
        },
      });
      // Refresh from server so the list + slot counts stay perfectly
      // consistent (source of truth).
      await fetchSavedBackgrounds();
      // If the replaced tile was the active goal's selection, hop the selection over.
      setSelectedBackgroundIdForGoal(prev => (prev === replaceId ? saved.background_id : prev));
      toast.success('Media replaced successfully.');
    } catch (err) {
      if (err && err.aborted) {
        toast.info('Replacement cancelled — original media unchanged.');
      } else {
        toast.error(err.message || 'Replacement failed — original media unchanged.');
      }
    } finally {
      setUploadingSavedBg(false);
      setUploadPhase(null);
      setUploadProgress(0);
      if (replaceInputRef.current) replaceInputRef.current.value = '';
    }
  };

  // Save preset
  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/visualization/presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: presetName.trim(),
          duration: selectedDuration,
          sound_id: selectedSound.id,
          background_id: selectedBg.id,
          // Blob URLs are session-only; persistent uploads are referenced via saved_background_id
          slideshow_urls: slideshowImages.filter(url => !url.startsWith('blob:')),
          slideshow_interval: slideshowInterval,
          voice_id: selectedVoice?.voice_id || null,
          affirmation_content: todaysAffirmation || null,
          affirmation_id: todaysAffirmationId || null,
          saved_background_id: activeSavedBgId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPresets([...presets, data.preset]);
        setPresetName('');
        setShowSaveModal(false);
        toast.success('Preset saved!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save preset');
      }
    } catch (error) {
      toast.error('Failed to save preset');
    }
  };

  // Restore a referenced saved background; blank if it no longer exists
  const restorePresetSavedBackground = async (backgroundId) => {
    let list = [];
    try {
      const res = await fetch(`${API_URL}/api/backgrounds/saved`, { credentials: 'include' });
      const data = res.ok ? await res.json() : null;
      list = data?.success ? (data.data.backgrounds || []) : [];
    } catch (_) { /* treat as missing */ }
    const bg = list.find(b => b.background_id === backgroundId && b.file_url && b.file_available !== false);
    clearCustomMedia();
    if (list.length) setSavedBackgrounds(list);
    if (!bg) {
      setSelectedBg(BLANK_BG);
      return;
    }
    setActiveSavedBgId(bg.background_id);
    const fullUrl = savedBgFullUrl(bg);
    if (bg.media_type === 'video') setCustomVideo(fullUrl);
    else setSelectedSavedBg(fullUrl);
  };

  // Load preset
  const loadPreset = async (preset) => {
    // Presets created before full-state support lack this key; restore only legacy fields for them.
    const isFullPreset = 'saved_background_id' in preset;

    if (preset.duration) {
      setSelectedDuration(preset.duration);
      setTimeLeft(preset.duration);
    }
    
    const sound = AMBIENT_SOUNDS.find(s => s.id === preset.sound_id);
    if (sound) setSelectedSound(sound);
    
    const bg = BACKGROUNDS.find(b => b.id === preset.background_id);
    if (bg) setSelectedBg(bg);

    if (isFullPreset) {
      const voice = availableVoices.find(v => v.voice_id === preset.voice_id);
      if (voice) {
        setSelectedVoice(voice);
        setVoiceAudioUrl(null);
      }
      if (preset.affirmation_content) {
        setTodaysAffirmation(preset.affirmation_content);
        setTodaysAffirmationId(preset.affirmation_id || `preset_${preset.preset_id}`);
        setVoiceAudioUrl(null);
      }
    }
    
    setShowPresetsModal(false);

    if (isFullPreset && preset.saved_background_id) {
      await restorePresetSavedBackground(preset.saved_background_id);
    } else {
      if (isFullPreset) clearCustomMedia();
      if (preset.slideshow_urls?.length > 0) {
        setSlideshowImages(preset.slideshow_urls);
        setSlideshowActive(preset.slideshow_urls.length > 1);
        setSlideshowInterval(preset.slideshow_interval || 10);
      }
    }
    
    toast.success(`Loaded "${preset.name}"`);
  };

  // Delete preset
  const deletePreset = async (presetId, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_URL}/api/visualization/presets/${presetId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        setPresets(presets.filter(p => p.preset_id !== presetId));
        toast.success('Preset deleted');
      }
    } catch (error) {
      toast.error('Failed to delete preset');
    }
  };

  // Switch active goal (premium only)
  const handleSwitchGoal = async (goalId) => {
    try {
      const response = await fetch(`${API_URL}/api/goals/set-active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ goal_id: goalId }),
      });

      if (response.ok) {
        // Fetch the new active goal
        const goalRes = await fetch(`${API_URL}/api/goals/active`, { credentials: 'include' });
        if (goalRes.ok) {
          const newGoal = await goalRes.json();
          setGoal(newGoal);
          // Update allGoals to reflect the change
          setAllGoals(prev => prev.map(g => ({
            ...g,
            is_active: g.goal_id === goalId
          })));
          toast.success('Switched to: ' + newGoal.title);
        }
        setShowGoalPicker(false);
      } else {
        throw new Error('Failed to switch goal');
      }
    } catch (error) {
      toast.error('Failed to switch goal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#8BAA9E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 bg-[#1C1C1E]"
      onClick={handleScreenTap}
      data-testid="visualization-page"
      style={{ 
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)'
      }}
    >
      {/* Background - Fixed: use object-contain for uploaded videos to prevent cropping */}
      {customVideo ? (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-contain bg-black"
          src={customVideo}
          loop
          muted={isMuted}
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          disablePictureInPicture
          autoPlay={isPlaying}
        />
      ) : slideshowActive && slideshowImages.length > 0 ? (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black"
        >
          <img 
            src={getCurrentBgUrl()} 
            alt="Slideshow"
            className="max-w-full max-h-full object-contain transition-all duration-1000"
          />
        </div>
      ) : (
        <div 
          className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
          style={{ 
            backgroundImage: getCurrentBgUrl() ? `url(${getCurrentBgUrl()})` : 'none',
            willChange: 'background-image'
          }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />

      {/* Voice/Audio Loading Indicators - hide voicePlaying indicator so it doesn't cover the written affirmation */}
      {isPlaying && (voiceLoading || voiceError || ((!isMuted) && (audioLoading || audioError))) && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[70]">
          <div className="bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
            {voiceLoading && (
              <>
                <Loader2 className="w-4 h-4 text-[#C6A75E] animate-spin" />
                <span className="text-[#C6A75E] text-xs font-sans">Generating voice...</span>
              </>
            )}
            {voiceError && !voiceLoading && (
              <>
                <AlertCircle className="w-4 h-4 text-[#CF6679]" />
                <span className="text-[#CF6679] text-xs font-sans">{voiceError}</span>
              </>
            )}
            {!voiceLoading && !voiceError && audioLoading && !isMuted && (
              <>
                <Loader2 className="w-4 h-4 text-[#8BAA9E] animate-spin" />
                <span className="text-[#DCD3CA] text-xs font-sans">Loading audio...</span>
              </>
            )}
            {!voiceLoading && !voiceError && audioError && !isMuted && (
              <>
                <AlertCircle className="w-4 h-4 text-[#C6A75E]" />
                <span className="text-[#C6A75E] text-xs font-sans">{audioError}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sound Picker Modal */}
      {showSoundPicker && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => { e.stopPropagation(); setShowSoundPicker(false); }}
        >
          <div 
            className="bg-[#252528] border border-white/10 rounded-[24px] p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-xl text-[#DCD3CA] mb-4">Ambient Sounds</h3>
            
            {['Healing', '432Hz', 'Nature', 'Meditation', 'Ambient'].map(category => {
              const sounds = AMBIENT_SOUNDS.filter(s => s.category === category);
              if (sounds.length === 0) return null;
              
              return (
                <div key={category} className="mb-4">
                  <p className="text-[#8BAA9E] text-xs uppercase tracking-wider mb-2 font-sans">{category}</p>
                  <div className="space-y-2">
                    {sounds.map(sound => (
                      <button
                        key={sound.id}
                        onClick={() => {
                          setSelectedSound(sound);
                          setShowSoundPicker(false);
                        }}
                        className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                          selectedSound.id === sound.id
                            ? 'bg-[#8BAA9E]/20 border border-[#8BAA9E]/30'
                            : 'bg-[#1C1C1E] hover:bg-[#1C1C1E]/80'
                        }`}
                      >
                        <span className="text-[#DCD3CA] font-sans text-sm">{sound.name}</span>
                        {selectedSound.id === sound.id && (
                          <Check className="w-4 h-4 text-[#8BAA9E]" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Unified Settings Modal - Sound & Background Selection */}
      {showSettingsModal && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => { e.stopPropagation(); setShowSettingsModal(false); }}
        >
          <div 
            className="bg-[#252528] border border-white/10 rounded-[24px] p-6 w-full max-w-md max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-xl text-[#DCD3CA] mb-6">Session Settings</h3>
            
            {/* Sound Selection Section */}
            <div className="mb-6">
              <p className="text-[#8BAA9E] text-xs uppercase tracking-wider mb-3 font-sans flex items-center gap-2">
                <Volume2 className="w-4 h-4" /> Ambient Sound
              </p>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {AMBIENT_SOUNDS.map(sound => (
                  <button
                    key={sound.id}
                    onClick={() => setSelectedSound(sound)}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                      selectedSound.id === sound.id
                        ? 'bg-[#8BAA9E]/20 border border-[#8BAA9E]/30'
                        : 'bg-[#1C1C1E] hover:bg-[#1C1C1E]/80'
                    }`}
                  >
                    <div>
                      <span className="text-[#DCD3CA] font-sans text-sm">{sound.name}</span>
                      <span className="text-[#7A7670] text-xs ml-2">({sound.category})</span>
                    </div>
                    {selectedSound.id === sound.id && (
                      <Check className="w-4 h-4 text-[#8BAA9E]" />
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Voice Selection Section (Premium) */}
            {user?.is_premium && todaysAffirmation && availableVoices.length > 0 && (
              <div className="mb-6">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-3 font-sans flex items-center gap-2">
                  <Mic className="w-4 h-4" /> Affirmation Voice
                </p>
                <p className="text-[#7A7670] text-xs mb-3 font-sans">
                  Choose a voice to read your daily affirmation
                </p>
                <div className="space-y-2">
                  {availableVoices.map(voice => (
                    <button
                      key={voice.voice_id}
                      onClick={() => {
                        setSelectedVoice(voice);
                        // Clear cached audio when voice changes
                        setVoiceAudioUrl(null);
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                        selectedVoice?.voice_id === voice.voice_id
                          ? voice.is_silent 
                            ? 'bg-[#666666]/20 border border-[#666666]/30'
                            : 'bg-[#C6A75E]/20 border border-[#C6A75E]/30'
                          : 'bg-[#1C1C1E] hover:bg-[#1C1C1E]/80'
                      }`}
                      data-testid={`voice-option-${voice.voice_id}`}
                    >
                      <div className="flex items-center gap-2">
                        {voice.is_silent ? (
                          <VolumeX className="w-4 h-4 text-[#7A7670]" />
                        ) : (
                          <Volume2 className="w-4 h-4 text-[#C6A75E]" />
                        )}
                        <div>
                          <span className={`font-sans text-sm ${voice.is_silent ? 'text-[#B5B1AB]' : 'text-[#DCD3CA]'}`}>
                            {voice.name}
                          </span>
                          <span className="text-[#7A7670] text-xs ml-2">({voice.description})</span>
                        </div>
                      </div>
                      {selectedVoice?.voice_id === voice.voice_id && (
                        <Check className={`w-4 h-4 ${voice.is_silent ? 'text-[#7A7670]' : 'text-[#C6A75E]'}`} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Background Selection Section */}
            <div className="mb-6">
              <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-3 font-sans flex items-center gap-2">
                <Image className="w-4 h-4" /> Background
              </p>
              <div className="grid grid-cols-3 gap-2">
                {BACKGROUNDS.map((bg) => (
                  <button
                    key={bg.id}
                    onClick={() => {
                      clearCustomMedia();
                      setSelectedBg(bg);
                    }}
                    className={`relative aspect-video rounded-xl overflow-hidden border-2 transition-all ${
                      !customVideo && !selectedSavedBg && slideshowImages.length === 0 && selectedBg.id === bg.id
                        ? 'border-[#8BAA9E]'
                        : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                    title={bg.name}
                  >
                    <img 
                      src={bg.url} 
                      alt={bg.name} 
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {!customVideo && !selectedSavedBg && slideshowImages.length === 0 && selectedBg.id === bg.id && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              {/* Saved Backgrounds Section (Premium - Persistent) */}
              {user?.is_premium && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[#C6A75E] text-xs uppercase tracking-wider font-sans flex items-center gap-2">
                      <Save className="w-4 h-4" /> Saved Backgrounds
                    </p>
                    <span className="text-[#7A7670] text-xs font-sans">
                      {savedBgSlots.used}/{savedBgSlots.used + savedBgSlots.available} slots used
                    </span>
                  </div>
                  
                  {savedBgLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 text-[#C6A75E] animate-spin" />
                    </div>
                  ) : (
                    <>
                      {savedBackgrounds.length === 0 && (
                        <p className="text-[#7A7670] text-xs font-sans text-center pb-3">
                          Upload up to 3 photos or videos — they follow you across every goal.
                        </p>
                      )}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {Array.from({ length: 3 }).map((_, slotIndex) => {
                          const bg = savedBackgrounds[slotIndex];

                          // Empty slot → "+ Add" tile that opens the file picker
                          if (!bg) {
                            const canAdd = !uploadingSavedBg && savedBgSlots.available > 0
                              && slotIndex === savedBackgrounds.length;
                            const isNextSlot = slotIndex === savedBackgrounds.length;
                            return (
                              <button
                                key={`empty-${slotIndex}`}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (uploadingSavedBg) {
                                    cancelActiveUpload();
                                    return;
                                  }
                                  if (canAdd) savedBgFileInputRef.current?.click();
                                }}
                                disabled={!canAdd && !uploadingSavedBg}
                                className={`relative aspect-video rounded-xl border-2 border-dashed flex items-center justify-center transition-all ${
                                  isNextSlot
                                    ? 'border-[#C6A75E]/60 bg-[#C6A75E]/5 hover:border-[#C6A75E] hover:bg-[#C6A75E]/10 cursor-pointer'
                                    : 'border-white/10 bg-transparent opacity-50 cursor-not-allowed'
                                }`}
                                data-testid={isNextSlot ? 'upload-saved-bg-btn' : `saved-bg-slot-${slotIndex + 1}-empty`}
                                aria-label={`Empty slot ${slotIndex + 1} of 3`}
                              >
                                {uploadingSavedBg && isNextSlot ? (
                                  <div className="flex flex-col items-center gap-1 px-2">
                                    <Loader2 className="w-4 h-4 text-[#C6A75E] animate-spin" />
                                    <span className="text-[#C6A75E] text-[10px] font-sans">{uploadProgress}%</span>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center gap-1">
                                    <Upload className={`w-4 h-4 ${isNextSlot ? 'text-[#C6A75E]' : 'text-[#7A7670]'}`} />
                                    <span className={`text-[10px] font-sans ${isNextSlot ? 'text-[#C6A75E]' : 'text-[#7A7670]'}`}>
                                      Slot {slotIndex + 1}
                                    </span>
                                  </div>
                                )}
                              </button>
                            );
                          }

                          // Filled slot
                          const fullUrl = bg.file_url.startsWith('http') ? bg.file_url : `${API_URL}${bg.file_url}`;
                          const isSelected = (customVideo === fullUrl) || (selectedSavedBg === fullUrl) || activeSavedBgId === bg.background_id;
                          const isLost = bg.file_available === false;

                          return (
                            <div
                              key={bg.background_id}
                              className="relative group"
                              data-testid={`saved-bg-${bg.background_id}`}
                            >
                              <button
                                onClick={() => {
                                  if (isLost) {
                                    toast.error('This background was lost during a previous update. Please re-upload it.');
                                    return;
                                  }
                                  selectSavedBackground(bg);
                                }}
                                className={`relative aspect-video w-full rounded-xl overflow-hidden border-2 transition-all ${
                                  isSelected
                                    ? 'border-[#C6A75E]'
                                    : isLost
                                      ? 'border-[#CF6679]/40 opacity-60'
                                      : 'border-transparent opacity-80 hover:opacity-100'
                                }`}
                                title={isLost ? 'Background lost — please re-upload' : bg.name}
                                data-testid={isLost ? `saved-bg-lost-${bg.background_id}` : undefined}
                              >
                                {isLost ? (
                                  <div className="w-full h-full bg-[#252528] flex flex-col items-center justify-center gap-1 px-2 text-center">
                                    <X className="w-4 h-4 text-[#CF6679]" strokeWidth={2} />
                                    <span className="text-[#CF6679] text-[10px] font-sans leading-tight">Media lost —<br />re-upload</span>
                                  </div>
                                ) : bg.media_type === 'video' ? (
                                  <div className="w-full h-full bg-[#1C1C1E] relative group/video">
                                    <video
                                      src={fullUrl}
                                      className="w-full h-full object-cover"
                                      muted
                                      loop
                                      playsInline
                                      preload="metadata"
                                      onMouseEnter={(e) => e.target.play().catch(() => {})}
                                      onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover/video:bg-transparent transition-colors pointer-events-none">
                                      <Film className="w-5 h-5 text-[#C6A75E] group-hover/video:opacity-0 transition-opacity" />
                                    </div>
                                  </div>
                                ) : (
                                  <img
                                    src={fullUrl}
                                    alt={bg.name}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                )}
                                {isSelected && !isLost && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                    <Check className="w-5 h-5 text-white" />
                                  </div>
                                )}
                                {selectedBackgroundIdForGoal === bg.background_id && !isLost && (
                                  <div className="absolute bottom-0 inset-x-0 h-1 bg-[#C6A75E]" aria-hidden="true" />
                                )}
                              </button>
                              {/* Replace */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openReplacePicker(bg.background_id);
                                }}
                                className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-[#C6A75E] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Replace"
                                aria-label="Replace media"
                                data-testid={`replace-saved-bg-${bg.background_id}`}
                              >
                                <Upload className="w-3 h-3 text-[#1C1C1E]" />
                              </button>
                              {/* Delete */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSavedBackground(bg.background_id);
                                }}
                                className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#CF6679] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Delete"
                                aria-label="Delete media"
                                data-testid={`delete-saved-bg-${bg.background_id}`}
                              >
                                <X className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Hidden input for the per-tile Replace action */}
                  <input
                    ref={replaceInputRef}
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={handleReplaceFile}
                    data-testid="replace-saved-bg-input"
                  />

                  {/* Upload progress / cancel row (shown while an upload is in flight) */}
                  {uploadingSavedBg && (
                    <div className="w-full flex items-center justify-between gap-2 p-3 rounded-xl border border-[#C6A75E]/50 bg-[#C6A75E]/5">
                      <div className="flex items-center gap-2 min-w-0">
                        <Loader2 className="w-4 h-4 text-[#C6A75E] animate-spin flex-shrink-0" />
                        <span className="text-[#C6A75E] text-xs font-sans truncate">
                          {uploadPhase === 'uploading' && `Uploading… ${uploadProgress}%`}
                          {uploadPhase === 'finalizing' && 'Finalising…'}
                          {(!uploadPhase || uploadPhase === 'preparing') && 'Preparing…'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={cancelActiveUpload}
                        className="text-[#CF6679] text-xs font-sans underline flex-shrink-0"
                        data-testid="cancel-upload-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {!uploadingSavedBg && savedBgSlots.available === 0 && savedBackgrounds.length > 0 && (
                    <p className="text-[#7A7670] text-[11px] font-sans text-center mt-1">
                      All 3 slots used — replace or delete a tile to add another.
                    </p>
                  )}
                  <input
                    ref={savedBgFileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleUploadSavedBackground}
                    className="hidden"
                    data-testid="saved-bg-file-input"
                  />
                </div>
              )}
              
            </div>
            
            {/* Custom media indicator */}
            {(customVideo || slideshowImages.length > 0 || selectedSavedBg) && (
              <button
                onClick={(e) => { e.stopPropagation(); clearCustomMedia(); }}
                className="w-full text-[#CF6679] text-xs font-sans hover:underline mb-4"
              >
                Clear custom media
              </button>
            )}
            
            {/* Close button */}
            <Button
              onClick={() => setShowSettingsModal(false)}
              className="w-full bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-xl py-3"
            >
              Done
            </Button>
          </div>
        </div>
      )}

      {/* Presets Modal */}
      {showPresetsModal && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => { e.stopPropagation(); setShowPresetsModal(false); }}
        >
          <div 
            className="bg-[#252528] border border-white/10 rounded-[24px] p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-xl text-[#DCD3CA] mb-4">Saved Presets</h3>
            
            {presets.length === 0 ? (
              <p className="text-[#7A7670] text-sm font-sans text-center py-6">
                No presets saved yet. Create your first preset!
              </p>
            ) : (
              <div className="space-y-2 mb-4">
                {presets.map(preset => {
                  const isFull = 'saved_background_id' in preset;
                  const voiceName = isFull ? availableVoices.find(v => v.voice_id === preset.voice_id)?.name : null;
                  const bgName = isFull && preset.saved_background_id
                    ? (savedBackgrounds.find(b => b.background_id === preset.saved_background_id)?.name || 'Personal background')
                    : BACKGROUNDS.find(b => b.id === preset.background_id)?.name;
                  const details = [
                    `${preset.duration / 60} min`,
                    AMBIENT_SOUNDS.find(s => s.id === preset.sound_id)?.name || 'Sound',
                    voiceName,
                    bgName,
                  ].filter(Boolean).join(' • ');
                  return (
                  <div
                    key={preset.preset_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => loadPreset(preset)}
                    onKeyDown={(e) => { if (e.key === 'Enter') loadPreset(preset); }}
                    className="w-full flex items-center justify-between gap-2 p-4 rounded-xl bg-[#1C1C1E] hover:bg-[#1C1C1E]/80 transition-all cursor-pointer"
                    data-testid={`preset-item-${preset.preset_id}`}
                  >
                    <div className="text-left min-w-0">
                      <p className="text-[#DCD3CA] font-sans text-sm">{preset.name}</p>
                      <p className="text-[#7A7670] text-xs font-sans" data-testid={`preset-details-${preset.preset_id}`}>
                        {details}
                      </p>
                    </div>
                    <button
                      onClick={(e) => deletePreset(preset.preset_id, e)}
                      className="shrink-0 p-2 rounded-full hover:bg-[#CF6679]/20 text-[#7A7670] hover:text-[#CF6679] transition-colors"
                      aria-label={`Delete preset ${preset.name}`}
                      data-testid={`preset-delete-${preset.preset_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
            
            <Button
              onClick={() => { setShowPresetsModal(false); setShowSaveModal(true); }}
              className="w-full mt-4 bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-xl py-3"
            >
              <Plus className="w-4 h-4 mr-2" />
              Save Current Settings
            </Button>
          </div>
        </div>
      )}

      {/* Save Preset Modal */}
      {showSaveModal && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => { e.stopPropagation(); setShowSaveModal(false); }}
        >
          <div 
            className="bg-[#252528] border border-white/10 rounded-[24px] p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-xl text-[#DCD3CA] mb-4">Save Preset</h3>
            
            <div className="bg-[#1C1C1E] rounded-xl p-4 mb-4">
              <p className="text-[#7A7670] text-xs uppercase tracking-wider mb-2 font-sans">Current Settings</p>
              <p className="text-[#DCD3CA] text-sm font-sans">
                {selectedDuration / 60} min • {selectedSound.name} • {activeSavedBgId ? 'Personal background' : selectedBg.name}
              </p>
            </div>
            
            <Input
              placeholder="Preset name (e.g., Morning Meditation)"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              className="w-full bg-[#1C1C1E] border-white/10 rounded-xl py-3 px-4 text-[#DCD3CA] placeholder-[#7A7670] mb-4"
              data-testid="preset-name-input"
            />
            
            <div className="flex gap-3">
              <Button
                onClick={() => setShowSaveModal(false)}
                variant="outline"
                className="flex-1 border-[#666666] text-[#B5B1AB] rounded-xl py-3"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePreset}
                className="flex-1 bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-xl py-3"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Goal Picker Modal - Premium Only */}
      {showGoalPicker && user?.is_premium && allGoals.length > 1 && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => { e.stopPropagation(); setShowGoalPicker(false); }}
        >
          <div 
            className="bg-[#252528] border border-white/10 rounded-[24px] p-6 w-full max-w-md max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-xl text-[#DCD3CA] mb-4">Switch Goal</h3>
            <p className="text-[#7A7670] text-sm font-sans mb-4">Select which goal to visualise</p>
            
            <div className="space-y-2">
              {allGoals.map(g => (
                <button
                  key={g.goal_id}
                  onClick={() => handleSwitchGoal(g.goal_id)}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl text-left transition-all ${
                    g.is_active
                      ? 'bg-[#8BAA9E]/20 border border-[#8BAA9E]/30'
                      : 'bg-[#1C1C1E] hover:bg-[#1C1C1E]/80'
                  }`}
                  data-testid={`goal-option-${g.goal_id}`}
                >
                  <Target className={`w-5 h-5 flex-shrink-0 ${g.is_active ? 'text-[#8BAA9E]' : 'text-[#7A7670]'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#DCD3CA] font-sans text-sm truncate">{g.title}</p>
                    <p className="text-[#7A7670] text-xs font-sans truncate">{g.identity_statement}</p>
                  </div>
                  {g.is_active && (
                    <Check className="w-4 h-4 text-[#8BAA9E] flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content Container - Scrollable */}
      <div className="relative h-full flex flex-col overflow-y-auto">
        {/* Header - with Settings moved to top-left */}
        <header className={`px-4 sm:px-6 pt-4 sm:pt-8 flex items-center justify-between transition-opacity duration-500 flex-shrink-0 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (audioRef.current) {
                  audioRef.current.pause();
                  audioRef.current.src = '';
                }
                navigate('/dashboard');
              }}
              className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-xl flex items-center justify-center text-[#DCD3CA] hover:bg-black/50"
              data-testid="close-visualization-btn"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Settings Button - Top Left for easy access */}
            <button
              onClick={(e) => { e.stopPropagation(); setShowSettingsModal(true); }}
              className="h-10 px-4 rounded-full bg-black/30 backdrop-blur-xl flex items-center gap-2 text-[#DCD3CA] hover:bg-black/50 transition-all"
              data-testid="settings-modal-btn"
            >
              <Settings2 className="w-4 h-4" />
              <span className="text-xs font-sans hidden sm:inline">{selectedSound.name}</span>
              {user?.is_premium && selectedVoice && (
                <span className="text-xs font-sans text-[#C6A75E] hidden sm:inline">• {selectedVoice.name}</span>
              )}
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Goal Switcher Button - Premium Only */}
            {user?.is_premium && allGoals.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowGoalPicker(true); }}
                className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-xl flex items-center justify-center text-[#C6A75E] hover:bg-black/50"
                data-testid="goal-picker-btn"
                title="Switch Goal"
              >
                <Target className="w-5 h-5" />
              </button>
            )}
            
            <button
              onClick={(e) => { e.stopPropagation(); setShowPresetsModal(true); }}
              className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-xl flex items-center justify-center text-[#DCD3CA] hover:bg-black/50"
              data-testid="presets-btn"
            >
              <FolderOpen className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleMuteToggle}
              className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-xl flex items-center justify-center text-[#DCD3CA] hover:bg-black/50"
              data-testid="mute-btn"
              aria-label={isMuted ? "Unmute audio" : "Mute audio"}
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 text-center min-h-0">
          {completed ? (
            <div className="animate-fade-in">
              <div className="w-20 h-20 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-[#8BAA9E]" />
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#DCD3CA] mb-4">Session Complete</h2>
              <p className="text-[#B5B1AB] font-sans mb-8">You've aligned with your future self</p>
              <Button
                onClick={(e) => { e.stopPropagation(); navigate('/dashboard'); }}
                className="bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-full px-8 py-6"
                data-testid="return-dashboard-btn"
              >
                Return to Dashboard
              </Button>
            </div>
          ) : (
            <>
              {/* Slideshow indicator */}
              {slideshowActive && slideshowImages.length > 1 && (
                <div className="absolute top-20 sm:top-24 left-1/2 -translate-x-1/2 flex items-center gap-1">
                  {slideshowImages.map((img, idx) => (
                    <div 
                      key={`slide-${img?.slice?.(-10) || idx}`}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentSlideIndex ? 'bg-[#D1C8BF] w-4' : 'bg-[#D1C8BF]/30'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Today's Affirmation - Primary Focus (NO Identity Statement) */}
              <div className={`w-full max-w-2xl transition-opacity duration-500 px-6 sm:px-8 ${showControls ? 'opacity-100' : 'opacity-70'}`}>
                {todaysAffirmation ? (
                  <>
                    <p className="text-[#C6A75E] text-xs sm:text-sm tracking-[0.3em] uppercase mb-4 sm:mb-6 font-sans">
                      Today's Affirmation
                    </p>
                    <p 
                      className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl text-[#DCD3CA] leading-relaxed break-words text-center"
                      style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                      data-testid="affirmation-display"
                    >
                      "{todaysAffirmation}"
                    </p>
                    {goal?.title && (
                      <p className="text-[#B5B1AB]/60 font-sans text-xs sm:text-sm mt-4 sm:mt-6">
                        {goal.title}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-[#8BAA9E] text-xs sm:text-sm tracking-[0.3em] uppercase mb-4 sm:mb-6 font-sans">
                      Visualize Your Goal
                    </p>
                    <p 
                      className="font-serif text-xl sm:text-2xl md:text-3xl text-[#DCD3CA] leading-relaxed break-words text-center"
                      data-testid="goal-title-display"
                    >
                      {goal?.title || "Set your intention"}
                    </p>
                  </>
                )}
              </div>

              {/* Timer - with proper spacing from controls */}
              <div className={`mt-6 sm:mt-10 mb-8 transition-opacity duration-500 ${showControls ? 'opacity-100' : 'opacity-40'}`}>
                <div className="relative w-28 h-28 sm:w-36 sm:h-36 mx-auto mb-4 sm:mb-6">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 144 144">
                    <circle cx="72" cy="72" r="66" fill="none" stroke="rgba(209, 200, 191, 0.1)" strokeWidth="4" />
                    <circle
                      cx="72" cy="72" r="66" fill="none" stroke="#8BAA9E" strokeWidth="4" strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 66}`}
                      strokeDashoffset={`${2 * Math.PI * 66 * (1 - progress / 100)}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-serif text-3xl sm:text-4xl text-[#DCD3CA]">{formatTime(timeLeft)}</span>
                  </div>
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#8BAA9E] flex items-center justify-center text-[#1C1C1E] hover:bg-[#A3C2B6] transition-all mx-auto"
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? <Pause className="w-6 h-6 sm:w-7 sm:h-7" fill="currentColor" /> : <Play className="w-6 h-6 sm:w-7 sm:h-7 ml-1" fill="currentColor" />}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Bottom Controls - Duration Selector Only (Settings moved to top-left) */}
        {!completed && (
          <div className={`px-4 sm:px-6 pb-6 sm:pb-8 pt-2 transition-opacity duration-500 flex-shrink-0 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {/* Duration Selector - minimal, non-blocking */}
            <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
              {TIMER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isPlaying) {
                      setSelectedDuration(option.value);
                      setTimeLeft(option.value);
                    }
                  }}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-sans transition-all ${
                    selectedDuration === option.value
                      ? 'bg-[#D1C8BF]/20 text-[#DCD3CA]'
                      : 'text-[#B5B1AB] hover:text-[#DCD3CA]'
                  }`}
                  disabled={isPlaying}
                  data-testid={`duration-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Upload progress indicator */}
            {(videoLoading || uploadingSavedBg) && uploadPhase && (
              <div className="mt-3 max-w-xs mx-auto" data-testid="upload-progress-card">
                <div className="h-1 bg-[#1C1C1E] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#C6A75E] transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[#7A7670] text-xs font-sans">
                    {uploadPhase === 'preparing' && 'Preparing…'}
                    {uploadPhase === 'uploading' && `Uploading… ${uploadProgress}%`}
                    {uploadPhase === 'finalizing' && 'Finalising…'}
                  </p>
                  {uploadPhase !== 'finalizing' && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); cancelActiveUpload(); }}
                      className="text-[#CF6679] text-xs font-sans hover:underline"
                      data-testid="cancel-upload-btn"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Slideshow interval selector */}
            {slideshowActive && slideshowImages.length > 1 && (
              <div className="flex items-center justify-center gap-2 mt-3">
                <Clock className="w-4 h-4 text-[#B5B1AB]" />
                <span className="text-[#B5B1AB] text-xs font-sans">Interval:</span>
                {SLIDESHOW_INTERVALS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={(e) => { e.stopPropagation(); setSlideshowInterval(opt.value); }}
                    className={`px-2 py-1 rounded text-xs font-sans transition-all ${
                      slideshowInterval === opt.value
                        ? 'bg-[#C6A75E]/20 text-[#C6A75E]'
                        : 'text-[#7A7670] hover:text-[#B5B1AB]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Visualization;
